import { Box, IconButton } from '@montugroup/design-system';
import Delete from '@mui/icons-material/Delete';
import { FormHelperText, FormLabel } from '@mui/material';
import React, { useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';

type SignatureFieldProps = {
  error?: boolean;
  height?: number;
  label?: string;
  onChange?: (data: string | null) => void;
  onClear?: () => void;
  value?: string | null;
  canvasRef: React.MutableRefObject<SignatureCanvas | null>;
};

export const Signature: React.FC<SignatureFieldProps> = ({ label, value, error, onChange, onClear, canvasRef }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleClear = () => {
    const canvas = canvasRef.current;

    if (canvas) {
      canvas.clear();

      if (onClear) {
        onClear();
      }
    }
  };

  const handleChange = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas?.toDataURL() ?? null;

    if (onChange) {
      onChange(dataUrl);
    }
  };

  return (
    <div>
      {label && (
        <FormLabel sx={{ fontSize: '14px', paddingLeft: '12px', color: error ? 'error.main' : 'text.grey' }}>
          {label}
        </FormLabel>
      )}
      <Box
        ref={wrapperRef}
        position="relative"
        sx={{
          border: 1,
          borderColor: error ? 'error.main' : 'grey.400',
          borderRadius: 1,
          cursor: value ? 'default' : 'crosshair',
          '&:hover': {
            borderColor: !error && !value ? 'grey.700' : undefined,
          },
          width: '100%',
          maxWidth: '100%',
          height: 180,
        }}
      >
        {value ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center', // vertical center
              justifyContent: 'center', // horizontal center
              objectFit: 'contain',
            }}
          >
            <img
              src={value}
              alt="Signature"
              style={{
                opacity: 0.5,
                pointerEvents: 'none',
                maxWidth: '99%',
                maxHeight: '99%',
              }}
            />
          </Box>
        ) : (
          <>
            <SignatureCanvas
              canvasProps={{
                style: {
                  width: '100%',
                  height: '100%',
                },
              }}
              ref={canvasRef}
              onEnd={handleChange}
            />

            <Box component="span" position="absolute" top={0} right={0}>
              <IconButton aria-label="Clear" onClick={handleClear}>
                <Delete />
              </IconButton>
            </Box>
          </>
        )}
      </Box>
      {error && <FormHelperText sx={{ color: 'error.main', pl: 1 }}>Signature required</FormHelperText>}
    </div>
  );
};
